import React, { useEffect, useRef } from 'react';
import { X } from 'phosphor-react';
import { useChatWidget } from '@/context/ChatWidgetContext';
import './ChatWidget.css';

const ChatWidget: React.FC = () => {
  const { isChatWidgetOpen } = useChatWidget();
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);

  useEffect(() => {
    if (isChatWidgetOpen && widgetContainerRef.current && !scriptLoadedRef.current) {
      console.log('Loading LeadConnectorHQ script dynamically...');

      // Check if script already exists in document
      const existingScript = document.querySelector('script[src*="leadconnectorhq.com/loader.js"]');
      if (existingScript) {
        console.log('Script already exists, not loading again');
        scriptLoadedRef.current = true;

        // Call openWidget since script is already loaded
        setTimeout(() => {
          if ((window as any).leadConnector?.chatWidget) {
            const chatWidget = (window as any).leadConnector.chatWidget;
            if (typeof chatWidget.openWidget === 'function') {
              console.log('Calling openWidget()...');
              chatWidget.openWidget();
            }
          }
        }, 500);
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.src = 'https://widgets.leadconnectorhq.com/loader.js';
      script.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js');
      script.setAttribute('data-widget-id', '69e0f08f25604d087a2a88b9');
      script.async = true;

      script.onload = () => {
        console.log('LeadConnectorHQ script loaded');
        scriptLoadedRef.current = true;

        // Call openWidget after script loads
        setTimeout(() => {
          if ((window as any).leadConnector?.chatWidget) {
            const chatWidget = (window as any).leadConnector.chatWidget;
            if (typeof chatWidget.openWidget === 'function') {
              console.log('Calling openWidget()...');
              chatWidget.openWidget();
            }
          }
        }, 500);
      };

      script.onerror = () => {
        console.error('Failed to load LeadConnectorHQ script');
      };

      // Append script to the widget container, not the body
      widgetContainerRef.current.appendChild(script);
    }

    // Clean up when modal closes
    return () => {
      if (!isChatWidgetOpen) {
        console.log('Disconnecting chat session...');

        // Disconnect the widget session
        if ((window as any).leadConnector?.chatWidget) {
          const chatWidget = (window as any).leadConnector.chatWidget;
          if (typeof chatWidget.closeWidget === 'function') {
            console.log('Calling closeWidget()...');
            chatWidget.closeWidget();
          }
          if (typeof chatWidget.disconnect === 'function') {
            console.log('Calling disconnect()...');
            chatWidget.disconnect();
          }
        }

        // Remove script if it exists in our container
        if (widgetContainerRef.current) {
          const script = widgetContainerRef.current.querySelector('script[src*="leadconnectorhq.com"]');
          if (script) {
            script.remove();
            scriptLoadedRef.current = false;
          }
        }

        // Clear the container content (but not the close button since it's outside)
        if (widgetContainerRef.current) {
          widgetContainerRef.current.innerHTML = '';
        }
      }
    };
  }, [isChatWidgetOpen]);

  return (
    <div className={`chat-widget-container ${isChatWidgetOpen ? 'open' : ''}`}>
      <div className="chat-widget-content">
        <button
          className="chat-widget-close"
          onClick={() => window.location.reload()}
          aria-label="Close chat"
        >
          <X size={20} weight="bold" />
        </button>
        <div ref={widgetContainerRef} className="chat-widget-inner">
          {/* LeadConnectorHQ widget will be loaded here dynamically */}
        </div>
      </div>
    </div>
  );
};

export default ChatWidget;
